import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { DisclosureRegion } from '../common';
import { AttentionSegment } from './AttentionSegment';
import { DutySegment } from './DutySegment';
import { ResidenceSegment } from './ResidenceSegment';
import { DUTY_LADDER, rungAfter, type DutyMode } from './dutyFit';
import type { AttentionItem } from './needsAttention';
import type { Staff } from '../staff/types';

export type StripPanel = 'attention' | 'duty' | 'residence' | null;

// Each panel's segment on the rail, so a close can hand focus back to the
// control that opened it.
const SEG_CLASS: Record<Exclude<StripPanel, null>, string> = {
  attention: 'attn',
  duty: 'duty',
  residence: 'res',
};

export interface CommandStripProps {
  attention: AttentionItem[];
  // Viewings still expected in the same look-ahead window — a count, on its own
  // axis. Threaded through rather than fetched here: the strip is presentation,
  // and TodayView already owns every windowed read on this page.
  attentionViewings?: number;
  attentionLoading?: boolean;
  attentionStale?: boolean;
  am: Staff[];
  pm: Staff[];
  residenceCount: number;
  // COLD LOAD only — nothing has ever arrived, so the segments show skeletons.
  // Never set this for a day step: swapping held content for shimmer is the
  // flicker every other component on this page avoids by holding and dimming.
  loading?: boolean;
  // A day step is resolving. The content on screen is the PREVIOUS day's and
  // stays exactly where it is — the page's own .is-stepping dim says it is not
  // settled. This only stops a panel being opened onto a set that is about to
  // be replaced.
  busy?: boolean;
  open: StripPanel;
  onOpen: (panel: StripPanel) => void;
  // ONE BODY PER PANEL, each in its own region. They are always mounted — that
  // is what lets a close animate rather than snap — and only the open one has
  // any height. Pass `null` for a panel whose data is not worth mounting yet.
  panels: Record<Exclude<StripPanel, null>, ReactNode>;
}

// The Daily page's command strip (daily-command-strip AC1–AC3, AC14).
//
// One 44px card carrying three segments, on the same surface, border and radius
// as the desk sections beneath it — a member of that stack rather than a banner
// above it. It never tints its background: urgency is carried by the glyph and
// the ink, which is how the desk rows already do it.
//
// THE RAIL HAS NO SPARE WIDTH. At 1280 logical, with the sidebar permanently
// collapsed, it is 1164 wide (2px of it the segments' own borders): In
// residence hugs at 85, Needs attention is fixed at 357, and On duty absorbs
// the remainder — 720 (681 when the attention box was 396).
//
// Needs attention is FIXED at 357 (Sir, 2026-09-02): sized to the three-kind
// day at single digits, so the chevron sits the same 9px after the last word
// as the first counter sits after the separator — and fixed, because a box
// that took what its counters needed moved the roster on every date step and
// read as a flicker. It grows only for a count at two digits or the capped
// triple, and the roster gives up the difference on those weeks alone. See dutyFit.ts for what that widening cost the roster: a rung, at
// 1280. In residence still hugs; On duty absorbs the remainder, which is why
// the air lands inside a disclosure row where air is ordinary rather than
// between two segments where it reads as a hole.

export function CommandStrip({
  attention,
  attentionViewings = 0,
  attentionLoading,
  attentionStale,
  am,
  pm,
  residenceCount,
  loading,
  busy,
  open,
  onOpen,
  panels,
}: CommandStripProps) {
  const railRef = useRef<HTMLDivElement>(null);
  // How far down the fit ladder the roster has had to step. Reset whenever the
  // inputs change, so a rail that once had to step down can step back up.
  const [step, setStep] = useState(0);

  // The roster prefers its FULL form — face and name inside a chip — and the
  // ladder decides what it actually gets. This said 'bare', which made 'full'
  // unreachable at any width: rungAfter only ever steps DOWN, so the richest
  // rung the ladder defines could never be rendered (PR #164, claude[bot] P2).
  const mode: DutyMode = rungAfter('full', step);

  // MEASURE, THEN STEP. Two kinds of clipping matter and self-measurement only
  // catches the first: an element can overflow its own box (scrollWidth tells
  // you), or it can fit its box perfectly while hanging outside an
  // overflow:hidden ancestor — invisible to everything the element can report
  // about itself. The rail overflowing counts too: with the duty segment sized
  // to its content it pushes the whole rail past its box without ever
  // overflowing its own.
  const measure = useCallback(() => {
    const rail = railRef.current;
    if (!rail) return;
    // The segment AND its two halves. Since the shifts split the width evenly
    // (`flex: 1 1 0`), a crowded half clips inside its own box without the
    // segment ever reporting overflow — the same blind spot that let a
    // guillotined chevron through, one level down.
    const duty = rail.querySelector<HTMLElement>('.cs-seg-duty');
    const boxes = [duty, ...rail.querySelectorAll<HTMLElement>('.cs-half')].filter(Boolean) as HTMLElement[];
    const overflows =
      rail.scrollWidth > rail.clientWidth + 1 ||
      boxes.some((el) => el.scrollWidth > el.clientWidth + 1);
    if (overflows) setStep((s) => Math.min(s + 1, DUTY_LADDER.length - 1));
  }, []);

  // Re-measure from the top whenever what is being drawn changes — a new
  // roster, a new width. Starting from step 0 each time is what lets the names
  // come back when the pressure that removed them goes away.
  // EVERY SEGMENT'S CONTENT, not just the roster's. Stepping between two days
  // with the same helpers but a different attention count or headcount changed
  // what the siblings occupy without changing this key, so a rail that had
  // stepped down for a wide neighbour stayed down after the neighbour shrank
  // (PR #164, codex P2). The measure pass below runs after every render and
  // catches the other direction; this is what lets the ladder climb back.
  const rosterKey =
    [...am, ...pm].map((s) => s.id).join(',') +
    '|' + String(loading) +
    '|' + String(attention.length) +
    // The third counter moves independently of `attention`, so it has to be in
    // the key: without it, settling a viewing left the rail showing the old
    // figure until something else on the strip happened to change.
    '|' + String(attentionViewings) +
    '|' + String(residenceCount);
  useLayoutEffect(() => {
    setStep(0);
  }, [rosterKey]);

  // ONE OBSERVER, FOR THE LIFE OF THE STRIP — and `step` is deliberately NOT a
  // dependency. It was, and the result was a permanent redraw cycle on every
  // screen where the roster does not fit at its richest rung: a step change
  // recreated the observer, a fresh observer fires an initial size notification
  // whether or not anything resized, that reset the ladder to the top, the top
  // overflowed, it stepped down, and round again — forever, with nobody
  // touching the window (PR #164, claude[bot] P2 + codex P2).
  //
  // Now a resize is distinguished from a re-observation by comparing the width
  // against the last one measured. Only a genuine change resets the ladder; the
  // initial notification simply re-measures at the rung already chosen.
  const lastWidth = useRef(0);
  useLayoutEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    lastWidth.current = rail.clientWidth;
    const ro = new ResizeObserver(() => {
      const w = rail.clientWidth;
      if (w === lastWidth.current) return; // a re-observation, not a resize
      lastWidth.current = w;
      // GIVE THE RICHEST RUNG ANOTHER CHANCE — and then look, because asking is
      // not the same as being told. `setStep(0)` when the step is ALREADY 0 is a
      // no-op: React bails out, nothing re-renders, and the layout effect that
      // does the measuring never runs. Every rich rung sits at step 0, so
      // dragging a window narrower changed nothing at all while dragging it
      // wider appeared to work — the step genuinely moved on the way up (Freddi,
      // 2026-08-17: "when im scaling the screen down the elements arent
      // readjusting"). Measured directly here, the shrink is caught on the spot
      // and the effect below converges from there.
      setStep(0);
      measure();
    });
    ro.observe(rail);
    return () => ro.disconnect();
  }, [measure]);

  // Measure after every paint that could have changed what is on the rail. This
  // is what actually steps the ladder down; it is separate from the observer so
  // that stepping cannot recreate it.
  useLayoutEffect(() => {
    measure();
  });

  // Escape closes. A keyboard user who opened a panel wants a key that shuts it.
  //
  // TWO THINGS IT MUST NOT DO. It must not close the panel underneath an open
  // modal — a booking opened FROM a panel put two Escape listeners on the same
  // key, so one press dismissed both and left the user two steps back from
  // where they meant to be (PR #164, codex P2). And it must not leave focus on
  // a control inside the region it just collapsed: the region becomes inert and
  // aria-hidden, so the next Tab starts from something invisible (codex P2).
  useEffect(() => {
    if (open === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // The modal owns the key while it is up. Two shells can be over this:
      // MasterDetailShell (`.obx` — the booking and helper modals, which is
      // what a panel row opens) and Modal.tsx (aria-modal). MasterDetailShell
      // carries no dialog role at all, which is its own a11y gap and not this
      // story's to close; matched on its class here rather than left unguarded.
      if (document.querySelector('.obx, [role="dialog"][aria-modal="true"]')) return;
      const trigger = railRef.current?.querySelector<HTMLElement>(`.cs-seg-${SEG_CLASS[open]} .cs-seg-btn`);
      onOpen(null);
      // After the close, not before — focusing a control inside a region about
      // to be made inert simply moves the problem.
      trigger?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpen]);

  // Nothing opens while the day is still resolving, or before it has arrived.
  //
  // The look-ahead settles INDEPENDENTLY of the day's own bookings, and after a
  // date jump it can still be answering the previous date when `busy` clears.
  // Opening the chase list at that moment shows yesterday's arrivals under
  // today's heading, so that one segment stays shut a moment longer than the
  // other two rather than all three waiting on the slowest (PR #164, codex P2).
  const toggle = (panel: Exclude<StripPanel, null>) => () => {
    if (loading || busy) return;
    if (panel === 'attention' && attentionStale && open !== 'attention') return;
    onOpen(open === panel ? null : panel);
  };

  return (
    <section className={'cs' + (open ? ' is-open' : '')} aria-label="Today at a glance">
      <div className="cs-rail" ref={railRef}>
        {/* COLD LOAD IS ONE PLAIN BLOCK, not the rail wearing its own furniture.
            It used to render the three segments with their glyphs, shift markers
            and chevrons in place and a shimmer where each value would be, on the
            reasoning that a segment which measures the same loading as loaded
            cannot resize under the cursor when data arrives. True, and it read
            oddly: a warning triangle and a paw sitting either side of pulsing
            bars is a strip half-dressed, where every other card on this page
            simply pulses (Freddi, 2026-08-18). The height is the rail's own, so
            nothing below it moves when the real segments take over.
            NOT the same thing as the attention segment's own skeleton, which
            survives below — that one runs while the seven-day look-ahead is
            still resolving and the rest of the strip is fully loaded, where a
            blank rail would be a lie about the two segments that do know. */}
        {loading ? (
          <span className="bl-block cs-sk-rail" aria-hidden="true" />
        ) : (
          <>
            <AttentionSegment
              items={attention}
              viewings={attentionViewings}
              loading={attentionLoading}
              stale={attentionStale}
              open={open === 'attention'}
              onToggle={toggle('attention')}
            />
            <DutySegment
              am={am}
              pm={pm}
              mode={mode}
              open={open === 'duty'}
              onToggle={toggle('duty')}
            />
            <ResidenceSegment
              count={residenceCount}
              open={open === 'residence'}
              onToggle={toggle('residence')}
            />
          </>
        )}
      </div>

      {/* THREE REGIONS, NOT ONE. A shared region has to swap its body as the
          open panel changes, which unmounts the outgoing content on the first
          frame and leaves the collapse animating an empty box — a close that
          snaps where the open slides. Each panel keeps its own region and its
          own permanently-mounted body, exactly as the rota's Hours & wages
          strip does, and only the open one has any height. */}
      <DisclosureRegion open={open === 'attention'}>{panels.attention}</DisclosureRegion>
      <DisclosureRegion open={open === 'duty'}>{panels.duty}</DisclosureRegion>
      <DisclosureRegion open={open === 'residence'}>{panels.residence}</DisclosureRegion>
    </section>
  );
}
